import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import { Input, PlusIcon, SelectField } from "components";
import {
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useCreateBillingTemplate,
  useUpdateBillingTemplate,
} from "controller/hooks/configurations/billingAndContracts/billingTemplate";
import {
  useGetAllDiscountsConfig,
  useGetAllTaxConfigs,
  useGetCompanySignature,
} from "controller";
import toast from "react-hot-toast";
import { TaxCreatModal } from "./TaxCreatModal";
import { PaymentTermOptions } from "shared";
import Decimal from "decimal.js";
import { QuillEditorWithChips } from "components/quill";
import { billingTemplateOptions } from "pages/modules/control-center/standardized-templates/email-template/emailTemplateComponents/quilEditorComponent/optionsDatas";
import { ChipSelectField } from "components/input/ChipSelectField";
import { debugConsole, isEmailValid } from "utilz/helpers";
import { DrawerComponent } from "./DrawerComponent";

const schema = yup.object({
  template_name: yup
    .string()
    .max(50, "Template name can't be more than 50 characters")
    .trim()
    .required("Template name is required"),
  payment_term: yup
    .number()
    .typeError("Payment term is required")
    .required("Payment term is required"),
  invoice_type: yup.string().required("Invoice type is required"),
  discount_is_percentage: yup.boolean().required("").default(true),
  discount_value: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .nullable()
    .notRequired()
    .typeError("Discount must be a number")
    .min(0, "Value must be at least 0%")
    .max(10000, "Value cannot exceed 100%"),
  discount_id: yup.string().nullable().notRequired(),
  tax: yup.string().required("Tax percent is required"),
  email_recipients_to: yup
    .array()
    .of(
      yup
        .string()
        .test("is-valid-email", "Must be a valid email", (value) =>
          value ? isEmailValid(value) : false,
        ),
    ),
  email_recipients_cc: yup
    .array()
    .of(
      yup
        .string()
        .test("is-valid-email", "Must be a valid email", (value) =>
          value ? isEmailValid(value) : false,
        ),
    ),
  email_subject: yup.string().trim().required("Email subject is required"),
  email_body: yup.string().required("Mail body is required"),
  add_signature: yup.boolean(),
  signature_id: yup.string(),
  signature_type: yup.string(),
});

const EMPTY_FORM_STATE = {
  template_name: "",
  payment_term: "",
  invoice_type: "",
  discount_is_percentage: true,
  discount_value: "0",
  discount_id: "",
  tax: "",
  email_recipients_to: [],
  email_recipients_cc: [],
  email_subject: "",
  email_body: "",
  add_signature: false,
  signature_id: "",
  signature_type: "",
};

export const BillingEmailFields = forwardRef(
  ({ closeDrawer, mode, isDrawerOpen, isAddSignature }: any, ref) => {
    useImperativeHandle(ref, () => ({
      saveButtonClick: () => {
        handleSubmit(
          async (data) => {
            // const discountData = Math.trunc(
            //   Number((Number(data.discount_value) * 100).toFixed(2))
            // );
            try {
              if (mode?.method === "create") {
                await createBillingMailTemplate({
                  ...data,
                  // discount_value: discountData,
                });
                toast.success("Billing template created successfully");
              } else if (mode?.method === "update") {
                await updateBillingMailTemplate({
                  data: {
                    ...data,
                    // discount_value: discountData,
                  },
                  id: mode?.data?.mapping_id,
                });
                toast.success("Billing template updated successfully");
              }
              closeDrawer();
              reset(EMPTY_FORM_STATE);
            } catch (error: any) {
              toast.error(
                error?.message
                  ? error?.message
                  : "Unable to add template at the moment. Please try again.",
              );
            }
          },
          (error) => {
            debugConsole(
              "🚀 ~ error:",
              error,
              "discount",
              getValues("discount_value"),
            );
          },
        )();
      },
      cancelButtonClick: () => {
        closeDrawer();
        reset(EMPTY_FORM_STATE);
      },
    }));
    const functionRef = useRef<any>(null);
    const { data: signatureData } = useGetCompanySignature();
    console.log("signatureData", signatureData);
    const { data: taxData, isFetching: isTaxDataLoading } = useGetAllTaxConfigs(
      {
        filter: [],
        // sortColumn: "tax_name",
        sortColumn: "created_at",
        sortDirection: "desc",
      },
    );

    useEffect(() => {
      if (taxData?.result) {
        defaultTaxCheck();
      }
    }, [taxData?.result, mode?.data?.tax, isDrawerOpen]);

    useEffect(() => {
      setValue("add_signature", isAddSignature);
      setValue(
        "signature_id",
        isAddSignature && signatureData?.mapping_id
          ? signatureData?.mapping_id
          : "",
      );
      setValue(
        "signature_type",
        isAddSignature && signatureData?.signature_type
          ? signatureData?.signature_type || "company"
          : "",
      );
    }, [isAddSignature, signatureData]);

    const defaultTaxCheck = async () => {
      if (!taxData?.result) return "";

      const res = taxData?.result?.find((item: any) => {
        return item.meta?.is_default === true;
      });
      // let res = taxData?.result?.find((item: any) => {
      //   item.mapping_id == "429adc2c-58c0-4e81-8516-7fa92ca30dac";
      //   return item;
      // });

      if (res) {
        setTimeout(() => {
          if (!mode?.data?.tax) {
            // @ts-ignore
            setValue("tax", res?.mapping_id);
          }
        }, 100);
        return res?.mapping_id;
      } else {
        return "";
      }
    };

    const buildDefaultValues = () => ({
      template_name: mode?.data?.template_name || "",
      payment_term: mode?.data?.payment_term || "",
      invoice_type: mode?.data?.invoice_type || "",
      discount_is_percentage: true,
      discount_value: mode?.data?.discount_value
        ? new Decimal(mode?.data?.discount_value).toNumber().toFixed(2)
        : "0",
      discount_id: mode?.data?.discount_id || "",
      tax: mode?.data?.tax || "",
      email_recipients_to: mode?.data?.email_recipients_to || [],
      email_recipients_cc: mode?.data?.email_recipients_cc || [],
      email_subject: mode?.data?.email_subject || "",
      email_body: mode?.data?.email_body || "",
      add_signature: mode?.data?.add_signature || isAddSignature || false,
      signature_id:
        (mode?.data?.signature_id || isAddSignature) &&
        signatureData?.mapping_id
          ? signatureData?.mapping_id
          : "",
      signature_type:
        (mode?.data?.signature_type || isAddSignature) &&
        (signatureData?.signature_type || "company")
          ? signatureData?.signature_type || "company"
          : "",
    });

    const {
      handleSubmit,
      control,
      reset,
      formState: { errors },
      setValue,
      getValues,
      watch,
    } = useForm({
      defaultValues: buildDefaultValues(),
      resolver: yupResolver(schema),
    });

    useEffect(() => {
      // Reset form whenever drawer opens or underlying data/signature context changes
      if (isDrawerOpen) {
        reset(buildDefaultValues());
      }
    }, [isDrawerOpen, JSON.stringify(mode?.data || {})]);

    const { mutateAsync: createBillingMailTemplate, isLoading } =
      useCreateBillingTemplate();
    const { mutateAsync: updateBillingMailTemplate } =
      useUpdateBillingTemplate();

    const theme = useTheme();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    // const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
    const [DrawerComponentData, setDrawerComponentData] = useState({
      open: false,
      componentName: "",
    });
    const [taxAddOnOptionList, setTaxAddOnOptionList] = useState<any>([]);
    const [latestTaxCreated, setLatestTaxCreated] = useState(null);

    const [openTax, setOpenTax] = useState(false);
    const [openDiscount, setOpenDiscount] = useState(false);

    const { data: discountData, isLoading: isDiscountLoading } =
      useGetAllDiscountsConfig({
        filter: [],
      });
    // const discount = watch("discount_value");

    // useEffect(() => {
    //   if (discountType == "true") {
    //     setDiscountPlaceholder("%");
    //   } else if (discountType == "false") {
    //     setDiscountPlaceholder("$");
    //   }
    // }, [discountType]);

    const closeDiscountModal = () => {
      setDrawerComponentData({ open: false, componentName: "" });
    };
    const { data: companySignature, isLoading: isCompanySignatureLoading } =
      useGetCompanySignature();
    const renderSignatureHtml = () => {
      return (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            border: "1px solid #EDEDED",
          }}
        >
          <Stack sx={{ width: "600px", height: "100%" }}>
            {companySignature?.signature_html && (
              <Box
                className="content-box"
                sx={{ p: "0px 0px", transition: "filter 0.2s" }}
                dangerouslySetInnerHTML={{
                  __html: companySignature?.signature_html || "", // need to ensure company signature is active
                }}
              />
            )}
          </Stack>
        </Box>
      );
    };

    useEffect(() => {
      setValue("tax", latestTaxCreated);
    }, [latestTaxCreated]);

    const getChipSelectFieldCustomStyles = (fieldName: string) => {
      return {
        width: "100%",
        fontFamily: theme.typography["body-medium"].fontFamily,
        fontWeight: theme.typography["body-medium"].fontWeight,
        fontSize: theme.typography["body-medium"].fontSize,
        height: "32px",
        "& .MuiInputBase-input": {
          height: "15px",
        },
        "& .MuiOutlinedInput-root": {
          color: "#1C1E26",
          py: "0px",
          px: "10px",
          // @ts-ignore
          backgroundColor: errors[fieldName] ? theme.palette.error.tint : "",
          "& > fieldset": {
            borderColor: "#CCCCCC",
            borderRadius: "4px",
          },
        },
        "& .MuiOutlinedInput-root.Mui-focused": {
          color: "#1C1E26",
          "& > fieldset": { borderColor: "#5563F5" },
        },
        ".Mui-error > fieldset": {
          borderColor: theme.palette.error.main,
        },
        "& .MuiInputLabel-root": {
          // @ts-ignore
          color: errors[fieldName] ? theme.palette.error.main : "#767B85",
          fontSize: "14px",
          transform:
            // @ts-ignore
            watch(`${fieldName}`)?.length > 0
              ? "translate(14px, -9px) scale(0.8)"
              : "translate(10px, 5px) scale(1)",

          "&.Mui-focused": {
            // @ts-ignore
            color: errors[fieldName] ? theme.palette.error.main : "#5563F5",
            fontWeight: "700",
            transform: "translate(14px, -9px) scale(1)",
            fontSize: "12px",
          },
        },
        "& .MuiAutocomplete-tag": {
          backgroundColor: "#E7EBF3",
        },
      };
    };

    return (
      <Stack
        sx={{
          p: "16px",
          width: "668px",
          gap: "24px",
        }}
      >
        <Stack
          sx={{
            p: "24px",
            gap: "16px",
            borderRadius: "8px",
            backgroundColor: theme.palette.neutral["005"],
          }}
        >
          <Field label="Template Name*">
            <Input
              name="template_name"
              control={control}
              error={errors?.template_name ? true : false}
              isResponsive={false}
              // @ts-ignore
              errorMessage={errors?.template_name?.message || ""}
              fullWidth
              inputProps={{
                style: {
                  backgroundColor: theme.palette.neutral["005"],
                },
              }}
              variant="filled"
            />
          </Field>
          <Field label="Invoice Type*">
            <SelectField
              name="invoice_type"
              control={control}
              options={[
                { type_name: "consolidated", type_value: "consolidated" },
                { type_name: "summarized", type_value: "summarized" },
                { type_name: "detailed", type_value: "detailed" },
              ]}
              labelMappingKey="type_name"
              valueMappingKey="type_value"
              error={errors?.invoice_type ? true : false}
              isResponsive={false}
              // @ts-ignore
              errorMessage={errors?.invoice_type?.message || ""}
              variant="filled"
              sx={{
                "&.MuiFilledInput-root": {
                  backgroundColor: theme.palette.neutral["005"],
                  "&:hover": {
                    backgroundColor: theme.palette.neutral["005"],
                  },
                },
              }}
            />
          </Field>
          <Field label="Payment Term*">
            <SelectField
              name="payment_term"
              control={control}
              options={PaymentTermOptions}
              labelMappingKey="label"
              valueMappingKey="value"
              error={errors?.payment_term ? true : false}
              isResponsive={false}
              // @ts-ignore
              errorMessage={errors?.payment_term?.message || ""}
              variant="filled"
              sx={{
                "&.MuiFilledInput-root": {
                  backgroundColor: theme.palette.neutral["005"],
                  "&:hover": {
                    backgroundColor: theme.palette.neutral["005"],
                  },
                },
              }}
            />
          </Field>
          <Field label="Tax (%) *">
            <SelectField
              name="tax"
              // control={control}
              options={taxData?.result || []}
              labelMappingKey="tax_name"
              valueMappingKey="mapping_id"
              error={errors?.tax ? true : false}
              open={openTax}
              onOpen={() => setOpenTax(true)}
              onClose={() => setOpenTax(false)}
              variant="filled"
              sx={{
                "&.MuiFilledInput-root": {
                  backgroundColor: theme.palette.neutral["005"],
                  "&:hover": {
                    backgroundColor: theme.palette.neutral["005"],
                  },
                },
              }}
              value={watch("tax")}
              onValueChange={(e) => {
                setValue("tax", e);
              }}
              renderValue={(tax_name, option) => {
                return (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body-medium"
                      sx={{
                        maxWidth: "200px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {option?.tax_name}
                    </Typography>
                    <Typography
                      variant="body-medium"
                      sx={{
                        minWidth: "100px",
                      }}
                    >
                      | &nbsp;&nbsp;&nbsp;
                      {option?.tax_rate &&
                        `${new Decimal(option?.tax_rate).div(100).toNumber()}%`}
                    </Typography>
                  </Box>
                );
              }}
              menuItemBuilder={({ item }) => (
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body-medium">{item.tax_name}</Typography>
                  <Typography variant="body-medium">
                    {new Decimal(item?.tax_rate).div(100).toNumber()}%
                  </Typography>
                </Box>
              )}
              isResponsive={false}
              // @ts-ignore
              errorMessage={errors?.tax?.message || ""}
              additionalPaperComponent={
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 20px 10px 20px",
                      position: "sticky",
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      setOpenTax(false);
                      setDrawerComponentData({
                        open: true,
                        componentName: "Add Tax",
                      });
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body-small"
                        sx={{
                          cursor: "pointer",
                          color: theme.palette.lightBg.low,
                        }}
                      >
                        Add New
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <PlusIcon
                        sx={{
                          fontSize: "26px",
                          color: theme.palette.lightBg.low,
                        }}
                      />
                    </Box>
                  </Box>
                  <Divider />
                </>
              }
            />
          </Field>
          <Field label="Discount (%)">
            <SelectField
              name="discount_id"
              // control={control}
              options={discountData?.result || []}
              labelMappingKey="discount_name"
              valueMappingKey="mapping_id"
              error={errors?.discount_id ? true : false}
              variant="filled"
              isClearable={true}
              sx={{
                "&.MuiFilledInput-root": {
                  backgroundColor: theme.palette.neutral["005"],
                  "&:hover": {
                    backgroundColor: theme.palette.neutral["005"],
                  },
                },
              }}
              value={watch("discount_id")}
              onValueChange={(e) => {
                setValue("discount_id", e);
              }}
              open={openDiscount}
              onOpen={() => setOpenDiscount(true)}
              onClose={() => setOpenDiscount(false)}
              renderValue={(discount_name, option) => {
                return (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body-medium"
                      sx={{
                        maxWidth: "200px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {option?.discount_name}
                    </Typography>
                    <Typography
                      variant="body-medium"
                      sx={{
                        minWidth: "100px",
                      }}
                    >
                      | &nbsp;&nbsp;&nbsp;{" "}
                      {option?.discount_rate && `${option?.discount_rate}%`}
                    </Typography>
                  </Box>
                );
              }}
              menuItemBuilder={({ item }) => (
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body-medium">
                    {item.discount_name}
                  </Typography>
                  <Typography variant="body-medium">
                    {item.discount_rate && `${item.discount_rate}%`}
                  </Typography>
                </Box>
              )}
              // @ts-ignore
              errorMessage={errors?.discount_id?.message || ""}
              additionalPaperComponent={
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 20px 10px 20px",
                      position: "sticky",
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      setOpenDiscount(false);
                      setDrawerComponentData({
                        open: true,
                        componentName: "Add Discount",
                      });
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body-small"
                        sx={{
                          cursor: "pointer",
                          color: theme.palette.lightBg.low,
                        }}
                      >
                        Add New
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <PlusIcon
                        sx={{
                          fontSize: "26px",
                          color: theme.palette.lightBg.low,
                        }}
                      />
                    </Box>
                  </Box>
                  <Divider />
                </>
              }
              customRhfOnChange={(value: any) => {
                setValue(
                  "discount_value",
                  (discountData?.result || []).find(
                    (item: any) => item.mapping_id === value,
                  )?.discount_rate || 0,
                );
              }}
            />
          </Field>
        </Stack>
        <Stack gap="16px">
          <Stack gap="4px">
            <ChipSelectField
              name="email_recipients_to"
              label={"To"}
              placeholder={"Enter email address"}
              control={control}
              hideHelperBox={true}
              error={errors?.email_recipients_to ? true : false}
              // @ts-ignore
              errorMessage={errors?.email_recipients_to?.message}
              customStyles={getChipSelectFieldCustomStyles(
                "email_recipients_to",
              )}
            />
            {errors?.email_recipients_to && (
              <Typography variant="body-small" color="error" ml="14px">
                Invalid email address
              </Typography>
            )}
          </Stack>
          <Stack gap="4px">
            <ChipSelectField
              name="email_recipients_cc"
              label={"Cc"}
              placeholder={"Enter email address"}
              control={control}
              hideHelperBox={true}
              error={errors?.email_recipients_cc ? true : false}
              // @ts-ignore
              errorMessage={errors?.email_recipients_cc?.message}
              customStyles={getChipSelectFieldCustomStyles(
                "email_recipients_cc",
              )}
            />
            {errors?.email_recipients_cc && (
              <Typography variant="body-small" color="error" ml="14px">
                Invalid email address
              </Typography>
            )}
          </Stack>
          {/* Chip editor rather than a plain input, so the subject carries the
              same variables as the body. Same configuration as the ticket email
              template's subject: no toolbar, no HTML menu. */}
          <Controller
            control={control}
            name="email_subject"
            render={({ field: { value, onChange } }) => (
              <QuillEditorWithChips
                value={value || ""}
                onChange={(content: string) => onChange(content)}
                placeholder="Mail Subject *"
                showToolbar={false}
                hideHtmlInsertMenu={true}
                minHeight="50px"
                chipOptions={billingTemplateOptions}
                error={errors?.email_subject ? true : false}
                // @ts-ignore
                errorMessage={errors?.email_subject?.message || ""}
              />
            )}
          />
        </Stack>
        <Box
          sx={{
            display: "block",
            maxWidth: "630px",
            mt: "-12px",
          }}
        >
          <Controller
            name="email_body"
            control={control}
            // render={({ field }) => (
            render={({ field: { value, onChange } }) => (
              <QuillEditorWithChips
                value={value || ""}
                onChange={(content: string) => onChange(content)}
                // {...field}
                chipOptions={billingTemplateOptions}
                showToolbar={true}
                minHeight="100px"
                ref={functionRef}
                additionalSignatureComponent={
                  isAddSignature && renderSignatureHtml()
                }
                error={errors?.email_body ? true : false}
                // @ts-ignore
                errorMessage={errors?.email_body?.message || ""}
              />
            )}
          />
        </Box>
        {isAddModalOpen && (
          <TaxCreatModal
            openModal={isAddModalOpen}
            setIsAddModalOpen={setIsAddModalOpen}
            taxAddOnOptionList={taxAddOnOptionList}
            setTaxAddOnOptionList={setTaxAddOnOptionList}
            setLatestTaxCreated={setLatestTaxCreated}
            setTaxValue={setValue}
          />
        )}
        {DrawerComponentData.open && (
          <DrawerComponent
            {...{
              isDrawerOpen: DrawerComponentData.open,
              closeDrawer: closeDiscountModal,
              componentName: DrawerComponentData.componentName,
              mode,
            }}
          />
        )}
      </Stack>
    );
  },
);

const Field = ({ label, children }: { label: string; children: ReactNode }) => {
  return (
    <Stack direction="row" alignItems="center" width="100%">
      <Typography variant="body-large" color="lightBg.low" width="196px">
        {label}:
      </Typography>
      <Box sx={{ width: "360px" }}>{children}</Box>
    </Stack>
  );
};

// useEffect(() => {
//   // Reset only when drawer opens or mode data changes. Preserve user edits.
//   if (isDrawerOpen) {
//     reset(buildDefaultValues(), { keepDirtyValues: true });
//   }
//   // eslint-disable-next-line react-hooks/exhaustive-deps
// }, [isDrawerOpen, JSON.stringify(mode?.data || {})]);

{
  /* <Box
sx={{
  display: "block",
  maxWidth: "630px",
  mt: "-12px",
}}
>
<Controller
  name="email_body"
  control={control}
  render={({ field: { value, onChange } }) => (
    <QuillEditorWithChips
      value={value || ""}
      onChange={(content: string) => onChange(content)}
      chipOptions={billingTemplateOptions}
      showToolbar={true}
      minHeight="100px"
      ref={functionRef}
      additionalSignatureComponent={
        isAddSignature && renderSignatureHtml()
      }
      error={errors?.email_body ? true : false}
      // @ts-ignore
      errorMessage={errors?.email_body?.message || ""}
    />
  )}
/>
</Box> */
}
